import { SuggestionData } from 'ckeditor5-premium-features'
import { ObjectBuilder } from 'components/WorkspaceEditor/factories'
import {
    SuggestionDataMap,
    useSuggestionsShallowSelector,
} from 'components/WorkspaceEditor/SuggestionStore'
import { TrackChangeType, TrackedChange } from 'models/changes'
import { useCallback, useMemo } from 'react'
import {
    buildSuggestionArray,
    buildSuggestionRecord,
    sortSuggestionArray,
} from '../utils/TrackChangesAndSuggestionsUtils'

/**
 * Custom hook to handle suggestion data creation and rendering from tracked changes.
 * It provides a function to create suggestion data from tracked changes and render suggestions based on their status.
 *
 * @returns {Object} An object containing the renderSuggestions function.
 */
export const useSuggestionDataHandler = () => {
    const {
        trackChangesData,
        addOrUpdateSuggestions,
    } = useSuggestionsShallowSelector((state) => ({
        trackChangesData: state.trackChangesData,
        addOrUpdateSuggestions: state.addOrUpdateSuggestions,
    }))

    const mapChangeType = (type: TrackChangeType): SuggestionData['type'] => {
        switch (type) {
            case TrackChangeType.ADDED:
                return 'insertion'
            case TrackChangeType.DELETED:
                return 'deletion'
            case TrackChangeType.MODIFIED:
                return 'modification'
            default:
                return type
        }
    }

    const toSuggestionData = useCallback(
        (value: TrackedChange, from?: string): SuggestionData => {
            const type =
                from === undefined
                    ? value.status
                    : mapChangeType(value.changeType)
            return new ObjectBuilder<SuggestionData>()
                .set('id', value.contentItemElementId)
                .set('type', type)
                .set('authorId', '')
                .set('attributes', {})
                .set(
                    'createdAt',
                    value.changeDateTime
                        ? new Date(value.changeDateTime)
                        : undefined
                )
                .set('data', {
                    key: value.contentItemElementId ?? '',
                    oldValue: value.changedText,
                    newValue: value.changedText,
                })
                .build()
        },
        []
    )

    const groupedChanges = useMemo(() => {
        const sortedByStatus: Record<string, TrackedChange[]> = {}
        for (const change of Object.values(trackChangesData)) {
            const status = change.status.toLowerCase()
            if (!sortedByStatus[status]) {
                sortedByStatus[status] = []
            }
            sortedByStatus[status].push(change)
        }

        return sortedByStatus
    }, [trackChangesData])

    const buildSortedSuggestionRecord = useCallback(
        (
            changes: TrackedChange[],
            from?: string
        ): Record<string, SuggestionDataMap> => {
            const suggestionArray = buildSuggestionArray(
                changes,
                toSuggestionData,
                from
            )
            const sortedArray = sortSuggestionArray(suggestionArray)
            return buildSuggestionRecord(sortedArray)
        },
        [toSuggestionData]
    )

    const renderSuggestions = useCallback(
        (status: string, from?: string) => {
            const normalizedStatus = status.toLowerCase()
            const changes = groupedChanges[normalizedStatus] || []
            const sortedSuggestionRecord = buildSortedSuggestionRecord(
                changes,
                from
            )
            addOrUpdateSuggestions(sortedSuggestionRecord, 'replace')
        },
        [groupedChanges, buildSortedSuggestionRecord, addOrUpdateSuggestions]
    )

    return { renderSuggestions }
}
